import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api } from '../api';
import { currentEmail } from '../auth';

interface Team {
  you: 'owner' | 'helper' | 'admin';
  owner: { email: string | null; name: string | null };
  helpers: { email: string; name: string | null; joined: boolean; invitedAtMs: number }[];
}

interface Activity {
  id: number;
  who: string;
  what: string;
  detail: string | null;
  entityType: string;
  entityId: string;
  atMs: number;
}

/** The people working on a race, each with their own login, and what each of them changed. */
export function TeamPage() {
  const { raceId = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const team = useQuery({ queryKey: ['team', raceId], queryFn: () => api<Team>(`/races/${raceId}/team`) });
  const activity = useQuery({ queryKey: ['activity', raceId], queryFn: () => api<{ activity: Activity[] }>(`/races/${raceId}/activity`), refetchInterval: 15_000 });
  const [email, setEmail] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const me = currentEmail()?.toLowerCase();

  const invite = useMutation({
    mutationFn: (to: string) => api<{ joined: boolean; emailed: boolean }>(`/races/${raceId}/team`, { body: { email: to } }),
    onSuccess: (r, to) => {
      setNote(
        r.joined
          ? `${to} already uses Splitline: the race is in their list now.`
          : r.emailed
            ? `We emailed ${to} a sign-in link and a temporary password. The race appears in their list when they sign in.`
            : `${to} will see the race the next time they sign in.`,
      );
      setEmail('');
      void qc.invalidateQueries({ queryKey: ['team', raceId] });
      void qc.invalidateQueries({ queryKey: ['activity', raceId] });
    },
  });
  const remove = useMutation({
    mutationFn: (who: string) => api(`/races/${raceId}/team/${encodeURIComponent(who)}`, { method: 'DELETE' }),
    onSuccess: (_r, who) => {
      if (who === me) navigate('/');
      else void qc.invalidateQueries({ queryKey: ['team', raceId] });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setNote(null);
    invite.mutate(email.trim());
  };

  const manage = team.data && team.data.you !== 'helper';

  return (
    <main className="page">
      <section className="page-head">
        <div>
          <h1>Team</h1>
          <p>Everyone helping on race day signs in with their own login, so every correction has a name next to it. Camera volunteers don’t need an account: they pair their phone with a station code.</p>
        </div>
      </section>
      <div className="row">
        <section className="card flush main-col" style={{ gap: 0 }}>
          <div className="card-head">
            <h2>People</h2>
          </div>
          {team.isLoading && <p className="empty">Loading…</p>}
          {team.data && (
            <div className="table-wrap">
              <table className="data">
                <tbody>
                  <tr>
                    <td>
                      <b>{team.data.owner.name ?? team.data.owner.email ?? 'Owner'}</b>
                      {team.data.owner.name && <div className="small muted">{team.data.owner.email}</div>}
                    </td>
                    <td>
                      <span className="chip info">Owner</span>
                    </td>
                    <td />
                  </tr>
                  {team.data.helpers.map((h) => (
                    <tr key={h.email}>
                      <td>
                        <b>{h.name ?? h.email}</b>
                        {h.name && <div className="small muted">{h.email}</div>}
                      </td>
                      <td>{h.joined ? <span className="chip ok">Helper</span> : <span className="chip warn">Invited, not signed in yet</span>}</td>
                      <td style={{ textAlign: 'right' }}>
                        {(manage || h.email === me) && (
                          <button
                            type="button"
                            className="btn small"
                            disabled={remove.isPending}
                            onClick={() => {
                              const self = h.email === me;
                              if (confirm(self ? 'Leave this race? You’ll lose access to it.' : `Remove ${h.email} from this race?`)) remove.mutate(h.email);
                            }}
                          >
                            {h.email === me ? 'Leave' : 'Remove'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {team.data && team.data.helpers.length === 0 && <p className="empty">No helpers yet.</p>}
          {remove.error && <div className="notice bad" style={{ margin: 16 }}>{(remove.error as Error).message}</div>}
        </section>

        <aside className="side-col stack">
          {manage ? (
            <form className="card stack" onSubmit={submit} style={{ gap: 12 }}>
              <h2>Invite a helper</h2>
              <label className="field">
                Email
                <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="helper@runningclub.org" required />
              </label>
              <p className="small muted" style={{ margin: 0 }}>
                Helpers can do everything on race day: run the dashboard, add runners, fire the gun, review crossings and fix results. Only you can invite or remove people and delete the race.
              </p>
              {invite.error && <div className="notice bad">{(invite.error as Error).message}</div>}
              {note && <div className="notice ok">{note}</div>}
              <button className="btn primary" type="submit" disabled={invite.isPending}>
                Invite
              </button>
            </form>
          ) : (
            team.data && (
              <section className="card stack">
                <h2>You’re a helper</h2>
                <p className="small muted" style={{ margin: 0 }}>
                  You can do everything on race day. Ask {team.data.owner.name ?? team.data.owner.email ?? 'the race’s owner'} to invite anyone else.
                </p>
              </section>
            )
          )}
        </aside>
      </div>

      <section className="card flush" style={{ gap: 0 }}>
        <div className="card-head">
          <h2>Activity</h2>
          <span className="small muted">Who changed what, newest first</span>
        </div>
        {activity.data && activity.data.activity.length === 0 && <p className="empty">Nothing yet.</p>}
        {activity.data && activity.data.activity.length > 0 && (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>What</th>
                </tr>
              </thead>
              <tbody>
                {activity.data.activity.map((a) => (
                  <tr key={a.id}>
                    <td className="mono small" style={{ whiteSpace: 'nowrap' }}>
                      {new Date(a.atMs).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </td>
                    <td>{a.who}</td>
                    <td>
                      {a.what}
                      {a.detail ? <span className="muted"> {a.detail}</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
